import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useTheme } from "next-themes";

import { useApi } from "@/hooks/useApi";
import { useAuth } from "@/components/AuthProvider";

/**
 * User-level appearance preferences. The server row is the durable source of
 * truth so preferences follow the user across browsers; localStorage (the
 * `theme` and `dark-mode-depth` keys) is the cache that paints on first load
 * before this query resolves.
 */
export type AppearanceSettings = {
  theme: string | null;
  dark_mode_depth: number | null;
};

export const APPEARANCE_SETTINGS_QUERY_KEY = ["appearance-settings"] as const;

export function useAppearanceSettings(enabled: boolean) {
  const api = useApi();

  return useQuery(
    APPEARANCE_SETTINGS_QUERY_KEY,
    async () => {
      const response = await api.get<AppearanceSettings>(
        "/user/appearance-settings",
      );
      return response.data;
    },
    {
      enabled,
      staleTime: Number.POSITIVE_INFINITY,
    },
  );
}

export function useUpdateAppearanceSettings() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation(
    async (update: Partial<AppearanceSettings>) => {
      const response = await api.put<AppearanceSettings>(
        "/user/appearance-settings",
        update,
      );
      return response.data;
    },
    {
      onSuccess: (data) => {
        queryClient.setQueryData(APPEARANCE_SETTINGS_QUERY_KEY, data);
      },
    },
  );
}

/**
 * Sets the app theme and persists it as the user's saved preference, so
 * theme changes made anywhere (sidebar toggle, command palette) survive the
 * server reconciliation on the next load.
 */
export function useSetPersistedTheme() {
  const { setTheme } = useTheme();
  const { user } = useAuth();
  const { mutate: persistSettings } = useUpdateAppearanceSettings();

  return useCallback(
    (theme: string) => {
      setTheme(theme);
      if (user) {
        persistSettings({ theme });
      }
    },
    [setTheme, user, persistSettings],
  );
}
